import axios from "axios";
import { jwtRefreshToken } from "./jwtAuthService";
import { getEnv } from "../envConfig";
import localStorageService from "./localStorageService";

const env = getEnv();

const axiosInstance = axios.create({
  baseURL: env.ENDPOINT || "",
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

/**
 * REQUEST INTERCEPTOR
 * Attaches Bearer token to every API request
 */
axiosInstance.interceptors.request.use(
  async (config) => {
    // Get access token from storage
    const token = await localStorageService.getItem("access_token");

    // Attach token to Authorization header
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      console.warn("⚠️ No access token found");
    }

    return config;
  },
  (error) => {
    console.error("❌ Request Error:", error);
    return Promise.reject(error);
  }
);

/**
 * RESPONSE INTERCEPTOR
 * Handles 401 errors by refreshing token and retrying request
 */
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    console.error("❌ API Error:", {
      status: error.response?.status,
      url: error.config?.url,
      method: error.config?.method,
      message: error.message,
      data: error.response?.data,
    });

    // Skip token refresh for auth endpoints (login, refresh, logout)
    const authEndpoints = [
      "/api/restaurant/auth/login",
      "/api/restaurant/auth/refresh",
      "/api/restaurant/auth/logout",
      "/api/restaurant/auth/register",
    ];
    const isAuthEndpoint = authEndpoints.some((endpoint) =>
      originalRequest.url?.includes(endpoint)
    );

    // Handle 401 Unauthorized - try to refresh token (skip for auth endpoints)
    if (
      error.response &&
      error.response.status === 401 &&
      !originalRequest._retry &&
      !isAuthEndpoint
    ) {
      originalRequest._retry = true;

      try {
        // Refresh token using JWT
        const newAccessToken = await jwtRefreshToken();

        // Retry original request with new token
        if (newAccessToken) {
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return axiosInstance(originalRequest);
        }
      } catch (refreshError) {
        console.error("❌ Token refresh failed:", refreshError);

        // If refresh token is expired, logout and redirect
        if (refreshError.message === "REFRESH_TOKEN_EXPIRED") {
          await localStorageService.removeItem("access_token");
          await localStorageService.removeItem("refresh_token");
          await localStorageService.removeItem("user");

          window.location.href = "/login";

          return Promise.reject(
            new Error("Session expired. Please login again.")
          );
        }

        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance; 